import { useState, useEffect, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Network, Search, Users, Mail, Building2, User, ZoomIn, ZoomOut,
  ChevronDown, ChevronRight, GitBranch, LayoutGrid, Phone, MapPin
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const DEPT_COLORS = {
  "Tecnología": { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" },
  "Recursos Humanos": { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300" },
  "Ventas": { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300" },
  "Marketing": { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-300" },
  "Finanzas": { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-300" },
  "Operaciones": { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-300" },
  "Legal": { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-300" },
  "Administración": { bg: "bg-slate-200", text: "text-slate-700", border: "border-slate-400" },
};
const DEFAULT_DC = { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300" };

export default function Organigrama() {
  const [nodes, setNodes] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [centerFilter, setCenterFilter] = useState("all");
  const [tab, setTab] = useState("diagram");
  const [zoom, setZoom] = useState(1);
  const [expandedDiagram, setExpandedDiagram] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const h = token ? { Authorization: "Bearer " + token } : {};
      const [orgRes, dirRes] = await Promise.all([
        axios.get(API + "/organigrama", { headers: h }),
        axios.get(API + "/directorio", { headers: h }),
      ]);
      const orgNodes = orgRes.data.nodes || [];
      setNodes(orgNodes);
      setDirectory(dirRes.data || []);
      // Expand root nodes by default
      const init = {};
      orgNodes.forEach(n => { if (!n.parent_id) init[n.id] = true; });
      setExpandedDiagram(init);
    } catch { toast.error("Error al cargar organigrama"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const departments = useMemo(() => [...new Set(nodes.map(n => n.department).filter(Boolean))], [nodes]);
  const workCenters = useMemo(() => [...new Set(nodes.map(n => n.work_center_name).filter(Boolean))], [nodes]);

  // Filter nodes
  const filteredNodes = useMemo(() => {
    let filtered = nodes;
    if (deptFilter !== "all") {
      const deptIds = new Set(nodes.filter(n => n.department === deptFilter).map(n => n.id));
      const ancestorIds = new Set();
      const findAnc = (id) => { const nd = nodes.find(n => n.id === id); if (nd && nd.parent_id) { ancestorIds.add(nd.parent_id); findAnc(nd.parent_id); } };
      deptIds.forEach(id => findAnc(id));
      filtered = filtered.filter(n => deptIds.has(n.id) || ancestorIds.has(n.id));
    }
    if (centerFilter !== "all") {
      const centerIds = new Set(filtered.filter(n => n.work_center_name === centerFilter).map(n => n.id));
      const ancestorIds = new Set();
      const findAnc = (id) => { const nd = filtered.find(n => n.id === id); if (nd && nd.parent_id) { ancestorIds.add(nd.parent_id); findAnc(nd.parent_id); } };
      centerIds.forEach(id => findAnc(id));
      filtered = filtered.filter(n => centerIds.has(n.id) || ancestorIds.has(n.id));
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchIds = new Set(filtered.filter(n => n.name?.toLowerCase().includes(term) || n.position?.toLowerCase().includes(term)).map(n => n.id));
      const ancestorIds = new Set();
      const findAnc = (id) => { const nd = filtered.find(n => n.id === id); if (nd && nd.parent_id) { ancestorIds.add(nd.parent_id); findAnc(nd.parent_id); } };
      matchIds.forEach(id => findAnc(id));
      filtered = filtered.filter(n => matchIds.has(n.id) || ancestorIds.has(n.id));
    }
    return filtered;
  }, [nodes, deptFilter, centerFilter, searchTerm]);

  // Build children map
  const childrenMap = useMemo(() => {
    const map = {};
    filteredNodes.forEach(n => { map[n.id] = []; });
    filteredNodes.forEach(n => { if (n.parent_id && map[n.parent_id]) map[n.parent_id].push(n); });
    return map;
  }, [filteredNodes]);

  const roots = useMemo(() => filteredNodes.filter(n => !n.parent_id || !filteredNodes.find(fn => fn.id === n.parent_id)), [filteredNodes]);

  const toggleDiagramExpand = (id) => setExpandedDiagram(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => { const all = {}; filteredNodes.forEach(n => { all[n.id] = true; }); setExpandedDiagram(all); };

  // Flatten for list view
  const flatList = useMemo(() => {
    const result = [];
    const stack = roots.map(r => ({ node: r, level: 0 })).reverse();
    while (stack.length > 0) {
      const item = stack.pop();
      const kids = childrenMap[item.node.id] || [];
      result.push({ ...item, hasChildren: kids.length > 0 });
      if (expandedDiagram[item.node.id] && kids.length > 0) {
        for (let i = kids.length - 1; i >= 0; i--) {
          stack.push({ node: kids[i], level: item.level + 1 });
        }
      }
    }
    return result;
  }, [roots, childrenMap, expandedDiagram]);

  const filteredDirectory = useMemo(() => directory.filter(e => {
    const term = searchTerm.toLowerCase();
    const matchSearch = !searchTerm || (e.first_name + " " + e.last_name).toLowerCase().includes(term) ||
      (e.email || "").toLowerCase().includes(term) || (e.position || "").toLowerCase().includes(term) ||
      (e.department || "").toLowerCase().includes(term);
    const matchDept = deptFilter === "all" || e.department === deptFilter;
    const matchCenter = centerFilter === "all" || e.work_center_name === centerFilter;
    return matchSearch && matchDept && matchCenter;
  }), [directory, searchTerm, deptFilter, centerFilter]);

  // Render a single diagram level (iterative tree rendering with nesting via CSS)
  const renderDiagramLevel = (nodeList, level) => {
    if (!nodeList || nodeList.length === 0) return null;
    return (
      <div className="flex gap-6 items-start justify-center flex-wrap">
        {nodeList.map(node => {
          const dc = DEPT_COLORS[node.department] || DEFAULT_DC;
          const kids = childrenMap[node.id] || [];
          const isExpanded = expandedDiagram[node.id];
          return (
            <div key={node.id} className="flex flex-col items-center" data-testid={`diagram-node-${node.id}`}>
              {/* Node Card */}
              <div
                className={`relative px-4 py-3 rounded-xl border-2 ${dc.border} ${dc.bg} shadow-sm hover:shadow-md transition-all cursor-pointer min-w-[180px] max-w-[220px]`}
                onClick={() => kids.length > 0 && toggleDiagramExpand(node.id)}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-full bg-white/80 flex items-center justify-center shrink-0 border ${dc.border}`}>
                    <span className={`text-xs font-bold ${dc.text}`}>{node.name?.split(" ").map(w => w[0]).join("").slice(0, 2)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 truncate leading-tight">{node.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{node.position}</p>
                  </div>
                </div>
                {node.department && <Badge className={`mt-1.5 text-[9px] py-0 px-1.5 ${dc.bg} ${dc.text} border ${dc.border}`}>{node.department}</Badge>}
                {node.work_center_name && <div className="mt-1 flex items-center gap-1 text-[10px] text-sky-700"><MapPin className="w-3 h-3" />{node.work_center_name}</div>}
                {kids.length > 0 && (
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center z-10">
                    {isExpanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
                  </div>
                )}
              </div>
              {/* Children connector + recursion (limited depth) */}
              {kids.length > 0 && isExpanded && level < 6 && (
                <div className="flex flex-col items-center">
                  <div className="w-px h-6 bg-slate-300" />
                  {kids.length > 1 && <div className="h-px bg-slate-300 self-stretch" />}
                  {renderDiagramLevel(kids, level + 1)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return <Layout title="Organigrama"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout title="Organigrama" subtitle="Estructura organizacional y directorio de colaboradores">
      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <TabsList data-testid="org-tabs">
            <TabsTrigger value="diagram" data-testid="tab-diagram"><GitBranch className="w-4 h-4 mr-2" />Diagrama</TabsTrigger>
            <TabsTrigger value="list" data-testid="tab-list"><LayoutGrid className="w-4 h-4 mr-2" />Lista</TabsTrigger>
            <TabsTrigger value="directory" data-testid="tab-directory"><Users className="w-4 h-4 mr-2" />Directorio</TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar empleado..." className="pl-10 w-[200px]" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="org-search" />
            </div>
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[180px]" data-testid="org-dept-filter"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={centerFilter} onValueChange={setCenterFilter}>
              <SelectTrigger className="w-[180px]" data-testid="org-center-filter"><SelectValue placeholder="Centro" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los centros</SelectItem>
                {workCenters.map(center => <SelectItem key={center} value={center}>{center}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* DIAGRAM */}
        <TabsContent value="diagram">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><GitBranch className="w-5 h-5 text-emerald-600" />Diagrama Organizacional</CardTitle>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.min(z + 0.1, 1.5))} data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setZoom(z => Math.max(z - 0.1, 0.4))} data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setZoom(1)}>100%</Button>
                  <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-btn">Expandir Todo</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {roots.length === 0 ? (
                <div className="text-center py-16 text-slate-500" data-testid="diagram-empty">
                  <Network className="w-14 h-14 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No hay datos del organigrama</p>
                  <p className="text-sm mt-1">Agrega empleados con un Jefe Inmediato asignado para ver el diagrama</p>
                </div>
              ) : (
                <div className="overflow-auto pb-8 pt-4" style={{ maxHeight: "70vh" }}>
                  <div className="min-w-max flex flex-col items-center" style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}>
                    {renderDiagramLevel(roots, 0)}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex flex-wrap gap-2 mt-4">
            {departments.map(d => {
              const dc = DEPT_COLORS[d] || DEFAULT_DC;
              return <Badge key={d} className={`${dc.bg} ${dc.text} border ${dc.border} text-xs`}>{d} ({nodes.filter(n => n.department === d).length})</Badge>;
            })}
          </div>
        </TabsContent>

        {/* LIST */}
        <TabsContent value="list">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Vista Jerárquica</CardTitle>
            </CardHeader>
            <CardContent>
              {flatList.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="list-empty"><Network className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">Sin datos</p></div>
              ) : (
                <div className="space-y-1">
                  {flatList.map(item => {
                    const dc = DEPT_COLORS[item.node.department] || DEFAULT_DC;
                    return (
                      <div key={item.node.id} style={{ paddingLeft: item.level * 32 }} data-testid={`org-node-${item.node.id}`}>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200 hover:shadow-sm transition-shadow cursor-pointer"
                          onClick={() => item.hasChildren && toggleDiagramExpand(item.node.id)}>
                          {item.hasChildren ? (expandedDiagram[item.node.id] ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />) : <div className="w-4" />}
                          <div className={`w-9 h-9 rounded-full ${dc.bg} flex items-center justify-center shrink-0`}><User className={`w-4 h-4 ${dc.text}`} /></div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 truncate">{item.node.name}</p>
                            <p className="text-xs text-slate-500 truncate">{item.node.position}</p>
                            {item.node.work_center_name && <p className="text-[11px] text-sky-600 truncate flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{item.node.work_center_name}</p>}
                          </div>
                          {item.node.department && <Badge className={`text-xs ${dc.bg} ${dc.text}`}>{item.node.department}</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DIRECTORY */}
        <TabsContent value="directory">
          {filteredDirectory.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="directory-empty"><Users className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium">No se encontraron colaboradores</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="directory-grid">
              {filteredDirectory.map(emp => {
                const dc = DEPT_COLORS[emp.department] || DEFAULT_DC;
                return (
                  <Card key={emp.employee_id} className="border-slate-200 hover:shadow-md transition-all" data-testid={`dir-card-${emp.employee_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full ${dc.bg} flex items-center justify-center`}><User className={`w-6 h-6 ${dc.text}`} /></div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-slate-900 truncate">{emp.first_name} {emp.last_name}</p>
                          <p className="text-sm text-slate-500 truncate">{emp.position}</p>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1.5">
                        {emp.department && <div className="flex items-center gap-2 text-sm text-slate-600"><Building2 className="w-3.5 h-3.5" /><span className="truncate">{emp.department}</span></div>}
                        {emp.work_center_name && <div className="flex items-center gap-2 text-sm text-sky-600"><MapPin className="w-3.5 h-3.5" /><span className="truncate">{emp.work_center_name}</span></div>}
                        {emp.email && <div className="flex items-center gap-2 text-sm text-slate-600"><Mail className="w-3.5 h-3.5" /><span className="truncate">{emp.email}</span></div>}
                        {emp.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-3.5 h-3.5" /><span className="truncate">{emp.phone}</span></div>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
